<script setup>
import { ref, computed, defineProps, onMounted, watch } from 'vue';
import TreeCheckboxDropdownItem from '@/components/scpm/components/TreeCheckboxDropdown/TreeCheckboxDropdownItem.vue';
import { useI18n } from 'vue-i18n';
import { useSwitcher } from '@/composables/useSwitcher';
import cScroll from '@/components/cmp/cScroll/cScroll.vue';

const { t } = useI18n({ useScope: 'global' });

// props.items
// @type [{icon: string, text: string, childItems: [{}], value: string}]

const props = defineProps({
  items: {
    type: Array,
    required: true
  },
  isExpandAll: {
    type: Boolean,
    default: false
  },
  selectedItems: {
    type: Array,
    required: false,
    default: () => []
  }
});

const emits = defineEmits(['tree-item-selected']);

const { isOn: isItemShowing, off: toggleItemOuter, toggle: toggleSelect } = useSwitcher();
const { isOn: isLoadedSelectedItems, on: setSelectedItemsLoaded } = useSwitcher();

const modifyItems = (items = [], config = {}) => {
  return items.map((item) => {
    if (Object.hasOwn(config, 'expand')) item.expand = config.expand;
    if (props.selectedItems?.includes(item.value) && !isLoadedSelectedItems.value) {
      item.isSelect = true;
    } else if (Object.hasOwn(config, 'isSelect')) item.isSelect = config.isSelect;

    if (item.childItems) item.childItems = modifyItems(item.childItems, config);

    return item;
  });
};

const transformToTreeItems = (items = []) => {
  const transformedItems = modifyItems(items, { expand: props.isExpandAll, isSelect: false });
  setSelectedItemsLoaded();

  return transformedItems;
};

const convertTreeToFlatItems = (treeItems = []) => {
  let result = [];
  for (const item of treeItems) {
    if (item.childItems) {
      result = result.concat(convertTreeToFlatItems(item.childItems));
    } else {
      result = result.concat(item);
    }
  }

  return result;
};

const items = ref(transformToTreeItems(props.items ?? []));
const flatItems = computed(() => convertTreeToFlatItems(items.value));

const initSelectedItems = computed(() => {
  if (!props.selectedItems) return [];
  const items = [];

  for (const selectedItem of props.selectedItems) {
    const item = flatItems.value.find((i) => i.value === selectedItem);
    if (item) items.push(item);
  }

  return items;
});

const selectedItems = ref(initSelectedItems.value);

const isCheckAll = computed(() => flatItems.value.every((item) => item.isSelect));
const isCheckEmpty = computed(() => flatItems.value.every((item) => !item.isSelect));
const isExpandAll = computed(() => flatItems.value.every((item) => item.expand));

const dropdownTitleStyle = computed(() =>
  selectedItems.value.length ? { color: '#000' } : { color: '#a5a7ad' }
);

const indeterminate = computed(
  () => !!(selectedItems.value.length && selectedItems.value.length < flatItems.value.length)
);

const selectedText = computed(() => {
  if (!selectedItems.value.length) return false;
  const firstText = selectedItems.value[0].text || '';
  const etc =
    selectedItems.value.length > 1 ? t('label.andOther', [selectedItems.value.length - 1]) : '';

  const fullText = etc ? firstText + ' ' + etc : firstText;

  return { firstText, etc, fullText };
});

const modifyItem = (items = [], itemKey = '', config = {}) => {
  for (const item of items) {
    if (item.value === itemKey) {
      if (Object.hasOwn(config, 'expand')) item.expand = config.expand;
      if (Object.hasOwn(config, 'isSelect')) item.isSelect = config.isSelect;
      break;
    }

    if (item.childItems) modifyItem(item.childItems, itemKey, config);
  }
  return items;
};

const toggleCheckAll = () => {
  items.value = modifyItems(items.value, { isSelect: !isCheckAll.value });
};

const toggleExpandAll = () => {
  items.value = modifyItems(items.value, { expand: !isExpandAll.value });
};

const toggleSelectItem = (item) => {
  items.value = modifyItem(items.value, { isSelect: !item.isSelect });
};

const toggleExpandItem = (item) => {
  items.value = modifyItem(items.value, { expand: !item.expand });
};

const clickItem = (item) => {
  if (item.childItems) toggleExpandItem(item);
  else toggleSelectItem(item);
};

const expandFirstChild = () => {
  if (props.isExpandAll) return;

  if (items.value && items.value.length && !selectedItems.value.length) {
    items.value[0].expand = !items.value[0].expand;
  }
};

watch(
  items,
  () => {
    const newSelectedItems = flatItems.value.filter((item) => item.isSelect);
    const diff = selectedItems.value
      .filter((item) => !newSelectedItems.find((i) => i.value === item.value))
      .concat(
        newSelectedItems.filter((item) => !selectedItems.value.find((i) => i.value === item.value))
      );

    if (isCheckAll.value) {
      selectedItems.value = flatItems.value;
    } else if (isCheckEmpty.value) {
      selectedItems.value = [];
    } else if (selectedItems.value.length < newSelectedItems.length) {
      selectedItems.value.push(diff[0]);
    } else if (selectedItems.value.length > newSelectedItems.length) {
      selectedItems.value = selectedItems.value.filter((item) => item.value !== diff[0].value);
    }
  },
  { deep: true }
);

watch(
  selectedItems,
  () => {
    const data = selectedItems.value.map((item) => item.value);
    emits('tree-item-selected', data);
  },
  { deep: true }
);

const handleExpand = (items) => {
  for (const node of items) {
    node.expand = node.isSelect;
    if (!node.childItems || !node.childItems.length) continue;
    handleExpand(node.childItems);
    node.expand = node.childItems.some((child) => child.expand);
  }
};
watch(isItemShowing, () => {
  if (isItemShowing) {
    handleExpand(items.value);
    expandFirstChild();
  }
});

onMounted(() => {
  expandFirstChild();
});
</script>

<template>
  <div class="lego-dropdown lego-input lego-tree-dropdown" v-click-outside="toggleItemOuter">
    <div
      class="lego-dropdown__title"
      :class="{
        'is-item-showing': isItemShowing
      }"
      @click="toggleSelect"
    >
      <div class="lego-dropdown__selected" :style="dropdownTitleStyle">
        <div class="lego-dropdown__selected-multiple" v-if="selectedItems.length && !isCheckAll">
          <span v-lego-tooltip:ellipsis="selectedText.fullText">
            {{ selectedText.firstText }}
          </span>
          <span>{{ selectedText.etc }}</span>
        </div>
        <span v-else>{{ $t('label.all') }}</span>
      </div>
      <lego-icon-wrapper class="lego-dropdown__title-icon">
        <lego-icon
          class="lego-dropdown__icon"
          icon-type="df"
          icon="arrow_down"
          size="16px"
          :style="caretStyle"
        />
      </lego-icon-wrapper>
    </div>

    <div class="lego-dropdown__downwrap" v-if="isItemShowing">
      <div class="lego-dropdown__tags">
        <div v-if="items === undefined || !items.length" class="no-result">
          {{ t('label.noSearchResult') }}
        </div>
        <template v-else>
          <div class="lego-dropdown__selectall-wrap">
            <div
              class="lego-dropdown__item lego-dropdown__selectall"
              :class="{ 'lego-dropdown__checked': isCheckAll }"
              @click="toggleCheckAll()"
            >
              <lego-checkbox
                v-model="isCheckAll"
                :indeterminate="indeterminate"
                @click="toggleCheckAll()"
                >{{ $t('label.all') }}
              </lego-checkbox>
            </div>
          </div>
          <c-scroll style="max-height: 288px">
            <tree-checkbox-dropdown-item
              v-for="(item, i) in items"
              :key="item.value"
              v-model="items[i]"
            />
          </c-scroll>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.no-result {
  cursor: default;
  padding: 25px 0;
  text-align: center;
  font-size: 11px;
  color: #858585;
}
.lego-dropdown {
  height: 32px;
  line-height: 24px;
  font-size: 14px;
  color: #2e3138;
  &__title {
    border-radius: 2px;
    &:hover {
      border-color: #2c59d9;
    }
    &.is-item-showing {
      border-color: $lego-dropdown__title__border--focus;
      .lego-icon {
        transform: rotate(-180deg);
      }
    }
    .lego-icon {
      transition: all ease-in-out 0.3s;
    }
    &.disabled-dropdown {
      background-color: #f4f4f6;
      border: 1px solid #e3e4e8;
      cursor: default;
    }
  }
  &__selected {
    display: flex;
    color: #a5a7ad;
    height: 32px;
    padding: 0 12px;

    span {
      display: inline-block;
      @include ellipsis;
    }

    &-multiple {
      display: flex;
      width: 100%;
      justify-content: flex-start;

      :last-child {
        padding-left: 4px;
        flex-shrink: 0;
      }
    }
  }
  &__downwrap {
    width: 100%;
  }
  &__tags {
    background-color: #ffffff;
    border: 1px solid #364fc7;
  }
  &__selectall {
    display: flex !important;
    border: 0;
    border-bottom: 1px solid #e4e5e7;
    line-height: 24px;
    min-height: 36px;
    max-height: 48px;
    font-weight: 700;
    align-items: center;
    padding: 6px 12px;
    :deep.lego-dropdown__item .lego-checkbox__checked + .lego-checkbox__label,
    .lego-checkbox__label {
      color: #2f3237 !important;
      padding-top: 1px;
    }
    .lego-checkbox {
      width: 100%;
    }
    &:hover {
      background-color: #eff1fb !important;
    }
  }
}
</style>
